import { FC, useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import type { CascaderProps } from "antd";
import {
  Form,
  Input,
  Button,
  message,
  Drawer,
  Row,
  Col,
  Card,
  Typography,
  Badge,
  Tooltip,
  Cascader,
  Image,
  Upload,
  Spin,
  TimePicker,
  Radio,
} from "antd";
import LocationSearchInput from "../../../templates/location-search-input";
import { formatAddress } from "../../../utils/functions";
import { QuestionCircleOutlined, UploadOutlined } from "@ant-design/icons";
import Map from "../../../templates/map";
import CustomMarker from "../../../templates/map/components/custom-marker";
import { Location, GMapsFormattedAddress, baseAddress } from "../../../types/location";
import { Option } from "../../../types/constants";
import { GET_ZONES } from "../addFranchise/requests";
import { UPDATE_LOCATION } from "./requests";
import UserSelectionForm from "../assignUsers";
import { typesUsers } from "../../../utils/constants";
import useUpload from "../../../hooks/use-upload";
import HolidayCalendar from "../addHoliday";
import moment from "moment-timezone";
import HolidayTable from "../holidayTable";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface EditLocationFormProps {
  editingLocation: Location | null;
  setEditingLocation: (value: Location | null) => void;
  refetchLocations: () => void;
}

const EditLocationForm: FC<EditLocationFormProps> = ({
  editingLocation,
  setEditingLocation,
  refetchLocations,
}) => {
  const [form] = Form.useForm();
  const { upload } = useUpload();
  const [updateLocation] = useMutation(UPDATE_LOCATION);
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedCascader, setSelectedCascader] = useState<string>("");
  const [destination, setDestination] = useState<GMapsFormattedAddress>(baseAddress);
  const [visible, setVisible] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Record<string, string[]>>({
    dropoff: [""],
    manager: [""],
    operation: [""],
  });
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadLoding, setUploadLoading] = useState(false);
  const [sameSchedule, setSameSchedule] = useState(false);
  const [holidays, setHolidays] = useState<any[]>([]);

  const { data } = useQuery(GET_ZONES, {
    variables: {
      params: {
        page: 1,
        pageSize: 20,
      },
    },
  });

  useEffect(() => {
    if (editingLocation) {
      setImageUrl(editingLocation.img ?? null)
      const updatedSelectedUsers = {
        dropoff: editingLocation.users.filter(user => user.type === typesUsers.dropoff.value).map(user => user.id),
        manager: editingLocation.users.filter(user => user.type === typesUsers.manager.value).map(user => user.id),
        operation: editingLocation.users.filter(user => user.type === typesUsers.operation.value).map(user => user.id),
      };
      setSelectedUsers(updatedSelectedUsers);

      form.setFieldsValue({
        ...editingLocation,
        subZone: editingLocation.subZone?.name,
        operations:{ integrations: {
          conekta:
          { conekta_id: editingLocation?.operations?.integrations?.conekta?.conekta_id },
        } },
        workingHours: {
          monday: editingLocation.operations?.workingHours?.monday?.open
            ? moment(editingLocation.operations.workingHours.monday.open)
            : null,
          mondayClose: editingLocation.operations?.workingHours?.monday?.close
            ? moment(editingLocation.operations.workingHours.monday.close)
            : null,
          tuesday: editingLocation.operations?.workingHours?.tuesday?.open
            ? moment(editingLocation.operations.workingHours.tuesday.open)
            : null,
          tuesdayClose: editingLocation.operations?.workingHours?.tuesday?.close
            ? moment(editingLocation.operations.workingHours.tuesday.close)
            : null,
          wednesday: editingLocation.operations?.workingHours?.wednesday?.open
            ? moment(editingLocation.operations.workingHours.wednesday.open)
            : null,
          wednesdayClose: editingLocation.operations?.workingHours?.wednesday?.close
            ? moment(editingLocation.operations.workingHours.wednesday.close)
            : null,
          thursday: editingLocation.operations?.workingHours?.thursday?.open
            ? moment(editingLocation.operations.workingHours.thursday.open)
            : null,
          thursdayClose: editingLocation.operations?.workingHours?.thursday?.close
            ? moment(editingLocation.operations.workingHours.thursday.close)
            : null,
          friday: editingLocation.operations?.workingHours?.friday?.open
            ? moment(editingLocation.operations.workingHours.friday.open)
            : null,
          fridayClose: editingLocation.operations?.workingHours?.friday?.close
            ? moment(editingLocation.operations.workingHours.friday.close)
            : null,
          saturday: editingLocation.operations?.workingHours?.saturday?.open
            ? moment(editingLocation.operations.workingHours.saturday.open)
            : null,
          saturdayClose: editingLocation.operations?.workingHours?.saturday?.close
            ? moment(editingLocation.operations.workingHours.saturday.close)
            : null,
          sunday: editingLocation.operations?.workingHours?.sunday?.open
            ? moment(editingLocation.operations.workingHours.sunday.open)
            : null,
          sundayClose: editingLocation.operations?.workingHours?.sunday?.close
            ? moment(editingLocation.operations.workingHours.sunday.close)
            : null,
        },
      });
      if (editingLocation.address) {
        setLocationSearch(editingLocation.address.fullName || "");
        setDestination({
          ...editingLocation.address,
        });
      }
      if (editingLocation.subZone?.id && data?.zones?.results) {
        const zones = data.zones.results;
        for (const zone of zones) {
          const foundSubZone = zone.subZones.find(
            (sz: any) => sz.id === editingLocation.subZone.id,
          );
          if (foundSubZone) {
            setSelectedCascader([zone.name, editingLocation.subZone.id]);
            break;
          }
        }
      }
      setHolidays(editingLocation.operations.holidays || []);
    }
  }, [editingLocation, data, form]);

  const cleanHolidays = (holidays: any[] = []) => {
    return holidays.map(holiday => {
      const cleaned = { ...holiday };
      delete cleaned.__typename;

      return {
        date: cleaned.date,
        repeat: cleaned.repeat,
        isOpen: cleaned.isOpen,
        hours: cleaned.hours ? {
          open: cleaned.hours.open,
          close: cleaned.hours.close,
        } : null,
      };
    });
  };

  const onFinish = async (values: Location) => {
    const usersIds = Object.values(selectedUsers).flat();

    let workingHours;

    if (sameSchedule) {
      const open = values.workingHours.allDaysOpen
        ? new Date(values.workingHours.allDaysOpen)
        : null;
      const close = values.workingHours.allDaysClose
        ? new Date(values.workingHours.allDaysClose)
        : null;

      workingHours = {
        monday: { open,
          close },
        tuesday: { open,
          close },
        wednesday: { open,
          close },
        thursday: { open,
          close },
        friday: { open,
          close },
        saturday: { open,
          close },
        sunday: { open,
          close },
      };
    } else {
      workingHours = {
        monday: {
          open: values.workingHours.monday
            ? new Date(values.workingHours.monday)
            : null,
          close: values.workingHours.mondayClose
            ? new Date(values.workingHours.mondayClose)
            : null,
        },
        tuesday: {
          open: values.workingHours.tuesday
            ? new Date(values.workingHours.tuesday)
            : null,
          close: values.workingHours.tuesdayClose
            ? new Date(values.workingHours.tuesdayClose)
            : null,
        },
        wednesday: {
          open: values.workingHours.wednesday
            ? new Date(values.workingHours.wednesday)
            : null,
          close: values.workingHours.wednesdayClose
            ? new Date(values.workingHours.wednesdayClose)
            : null,
        },
        thursday: {
          open: values.workingHours.thursday
            ? new Date(values.workingHours.thursday)
            : null,
          close: values.workingHours.thursdayClose
            ? new Date(values.workingHours.thursdayClose)
            : null,
        },
        friday: {
          open: values.workingHours.friday
            ? new Date(values.workingHours.friday)
            : null,
          close: values.workingHours.fridayClose
            ? new Date(values.workingHours.fridayClose)
            : null,
        },
        saturday: {
          open: values.workingHours.saturday
            ? new Date(values.workingHours.saturday)
            : null,
          close: values.workingHours.saturdayClose
            ? new Date(values.workingHours.saturdayClose)
            : null,
        },
        sunday: {
          open: values.workingHours.sunday
            ? new Date(values.workingHours.sunday)
            : null,
          close: values.workingHours.sundayClose
            ? new Date(values.workingHours.sundayClose)
            : null,
        },
      };
    }

    delete values.workingHours;

    try {
      await updateLocation({
        variables: {
          id: editingLocation?.id,
          location: {
            ...values,
            img: imageUrl,
            address: {
              ...values.address,
              location: {
                type: "Point",
                coordinates: values.address.location.coordinates,
              },
            },
            operations: {
              workingHours,
              holidays: cleanHolidays(holidays),
              integrations: {
                conekta: {
                  conekta_id: values.operations.integrations.conekta.conekta_id,
                },
              },
            },
            subZone: selectedCascader[selectedCascader.length - 1],
          },
          users: usersIds,
        },
      });

      message.success("Operación exitosa!");
      refetchLocations();
      setEditingLocation(null);
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    }
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `locations/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const onChange: CascaderProps<Option>["onChange"] = (value) => {
    if (value && value.length) {
      return setSelectedCascader([value[value.length - 1]]);
    }
    return setSelectedCascader([]);
  };

  const onClosed = () => {
    setDestination(baseAddress);
    form.resetFields();
    setLocationSearch("");
    setEditingLocation(null);
    setImageUrl(null);
    setSameSchedule(false);
    setHolidays([]);
  };

  return (
    <Drawer
      visible={!!editingLocation}
      width={"80vw"}
      title={
        <Text style={{ fontWeight: "800",
          fontSize: 18 }}>
          Editar Franquicia
        </Text>
      }
      onClose={onClosed}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={13}>
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
                borderRadius: "15px",
              }}
            >
              <Text style={{ fontSize: 15 }} strong>
                Nombre de Franquicia <Text style={{ color: "red" }}>*</Text>
              </Text>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese el nombre de la franquicia",
                  },
                ]}
              >
                <Input size="large" placeholder="Nombre de la franquicia" />
              </Form.Item>
              <Text style={{ fontSize: 15 }} strong>
                Teléfono <Text style={{ color: "red" }}>*</Text>
              </Text>
              <Form.Item
                name="phone"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese el teléfono de la franquicia",
                  },
                ]}
              >
                <Input size="large" placeholder="Teléfono de la franquicia" />
              </Form.Item>

              <Text style={{ fontSize: 15 }} strong>
                Subzona <Text style={{ color: "red" }}>*</Text>
              </Text>
              <Form.Item
                name="subZone"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese la subzona",
                  },
                ]}
              >
                <Cascader
                  size="large"
                  options={
                    data?.zones.results.map((zone) => ({
                      value: zone.name,
                      label: zone.name,
                      children: zone.subZones.map((subZone) => ({
                        value: subZone.id,
                        label: subZone.name,
                      })),
                    })) || []
                  }
                  onChange={onChange}
                  placeholder="Selecciona la subzona"
                />
              </Form.Item>

              <Text style={{ fontSize: 15 }} strong>
                Dirección Completa <Text style={{ color: "red" }}>*</Text>
              </Text>
              <LocationSearchInput
                placeholder="Ingresa la dirección completa para buscarla..."
                value={locationSearch}
                onChange={(value) => setLocationSearch(value)}
                onSelect={async (location) => {
                  if (!location) {
                    setLocationSearch("");
                    setDestination(baseAddress);
                  } else {
                    try {
                      const locationData = await formatAddress(location);
                      setDestination(locationData.destination);
                      setLocationSearch(
                        locationData.destination.fullName || "",
                      );
                      form.setFieldsValue({
                        address: {
                          fullName: locationData.destination.fullName,
                          street: locationData.destination.street,
                          colony: locationData.destination.colony,
                          postcode: locationData.destination.postcode,
                          city: locationData.destination.city,
                          country: locationData.destination.country,
                          state: locationData.destination.state,
                          exteriorNumber:
                            locationData.destination.exteriorNumber,
                          location: {
                            type: "Point",
                            coordinates:
                              locationData.destination.location?.coordinates ??
                              [],
                          },
                        },
                      });
                    } catch (error) {
                      console.error("Error formatting address:", error);
                    }
                  }
                }}
              />
              <Form.Item name={["address", "fullName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "street"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "colony"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "postcode"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "city"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "state"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "country"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "exteriorNumber"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "location", "type"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "location", "coordinates"]} hidden>
                <Input />
              </Form.Item>

              <Form.Item>
                <Badge.Ribbon
                  text={
                    <Tooltip
                      autoAdjustOverflow
                      placement="topRight"
                      title="Ingresa direcciones completas para asegurar que tu ubicación es correcta."
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  }
                  placement="start"
                >
                  <Row justify="center" style={{ textAlign: "center" }}>
                    <Col span={6}>
                      <Text strong>País</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Estado</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Ciudad</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Código postal</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{destination.country ?? "-"}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{destination.state ?? "-"}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{destination.city ?? "-"}</Text>
                    </Col>
                    <Col span={6} data-tut="zip_code">
                      <Text type="secondary">
                        {destination.postcode ?? "-"}
                      </Text>
                    </Col>
                  </Row>
                </Badge.Ribbon>
              </Form.Item>

              <Form.Item
                name={["address", "colony"]}
                rules={[
                  { required: true,
                    message: "Colonia es requerida" },
                ]}
              >
                <Input size="large" placeholder="Colonia" />
              </Form.Item>

              <Form.Item
                name={["address", "street"]}
                rules={[
                  { required: true,
                    message: "Calle es requerida" },
                ]}
              >
                <Input size="large" placeholder="Calle(s) y número" />
              </Form.Item>
            </Card>
          </Col>

          <Col span={10}>
            <Card
              style={{
                overflow: "hidden",
                boxShadow:
                  "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
                borderRadius: "15px",
              }}
              bodyStyle={{
                paddingLeft: "0",
                paddingRight: "0",
                paddingBottom: "0",
                paddingTop: "0",
              }}
            >
              <Map style={{ height: "38vh" }} zoom={10} addDestinationInfo={() => {}}>
                {destination?.location?.coordinates?.length && (
                  <CustomMarker
                    key={destination?.location?.coordinates.join("-")}
                    lng={destination?.location?.coordinates[0]}
                    lat={destination?.location?.coordinates[1]}
                    src={"public/logo-pollo-loco.png"}
                    title="Franquicia"
                    onClick={() => {}}
                  />
                )}
              </Map>
            </Card>
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
                borderRadius: "15px",
                marginTop: 15,
              }}
              bodyStyle={{
                paddingBottom: "0",
              }}
            >
              <div>
                <Text style={{ fontSize: 15,
                  marginRight: 10 }} strong>
                  {imageUrl ? " Cambiar Imagen" : "Imagen"}:
                </Text>
                <Upload
                  beforeUpload={() => false}
                  listType="picture"
                  maxCount={1}
                  accept="image/*"
                  onChange={(info) => {
                    handleFileUpload(info);
                  }}
                  showUploadList={false}
                >
                  <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
                  {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
                </Upload>
                {imageUrl && (
                  <Image style={{ marginLeft: 20 }} width={70} src={imageUrl} />
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <Text style={{ fontSize: 15 }} strong>
                  Instrucciones de Recoleción
                </Text>
                <Form.Item
                  name="collectionInstructions"
                >
                  <TextArea size="large" placeholder="Agregar instrucciones de recolección" />
                </Form.Item>
              </div>
            </Card>
          </Col>
        </Row>
        <Card
          title={<div>
            <Title level={4}>Asignar Usuarios</Title>
            <span>Se debe asignar al menos un tipo de usuario de Operador, Gerente y Repartidor.</span>
          </div>}
          style={{
            overflow: "hidden",
            boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
            borderRadius: "15px",
            marginTop: 15,
          }}
        >
          <UserSelectionForm
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
          />
        </Card>
        <Card
          title={<div>
            <Title level={4}>Asignar Horarios de Operacion</Title>
            <span>En este apartado se pueden seleccionar los horarios de operacion de la franquicia.</span>
          </div>}
          style={{
            overflow: "hidden",
            boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
            borderRadius: "15px",
            marginTop: 15,
          }}
        >
          <Form.Item label="¿Mismo horario todos los días?">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => setSameSchedule(e.target.value)}
              value={sameSchedule}
            >
              <Radio.Button value={true}>Sí</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {!sameSchedule && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Lunes" name={["workingHours", "monday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "mondayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label="Martes" name={["workingHours", "tuesday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "tuesdayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label="Miércoles" name={["workingHours", "wednesday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "wednesdayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Jueves" name={["workingHours", "thursday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "thursdayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Viernes" name={["workingHours", "friday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "fridayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Sábado" name={["workingHours", "saturday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "saturdayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Domingo" name={["workingHours", "sunday"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
                <Form.Item name={["workingHours", "sundayClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {sameSchedule && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Hora de apertura" name={["workingHours", "allDaysOpen"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de apertura"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Hora de cierre" name={["workingHours", "allDaysClose"]}>
                  <TimePicker
                    format="HH:mm"
                    use12Hours
                    minuteStep={15}
                    style={{ width: "100%" }}
                    placeholder="Hora de cierre"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Card>
        <Card
          title={<div>
            <Title level={4}>Días festivos y Asuetos</Title>
            <span>Apartado para agregar días festivos y asuetos</span>
          </div>}
          style={{
            overflow: "hidden",
            boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
            borderRadius: "15px",
            marginTop: 15,
          }}
        >
          <Row justify="end">
            <Button type="primary" style={{ margin: "5px" }} onClick={() => setVisible(true)}>
              Agregar Asueto
            </Button>
          </Row>
          {holidays.length > 0 ? (
            <HolidayTable holidays={holidays} setHolidays={setHolidays} locationId={editingLocation?.id} />
          ) : (
            <Text type="secondary">No hay asuetos asignados</Text>
          )}
        </Card>
        <Card
          title={<div>
            <Title level={4}>Integraciones</Title>
          </div>}
          style={{
            overflow: "hidden",
            boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
            borderRadius: "15px",
            marginTop: 15,
          }}
        >
          <Form.Item label="Conekta ID: " name={["operations", "integrations", "conekta", "conekta_id"]}>
            <Input
              size="large"
              placeholder="ID de Conekta"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Card>
      </Form>
      <Row justify="end" style={{ marginTop: "20px" }}>
        <Button
          style={{ width: "100%" }}
          disabled={
            !form.getFieldValue("name") ||
            selectedCascader.length === 0 ||
            !form.getFieldValue(["address", "fullName"]) ||
            !form.getFieldValue("phone") ||
            !Object.values(selectedUsers).every((userIds) =>
              Array.isArray(userIds) &&
              userIds.length > 0 &&
              userIds.every((id) => id !== ""))
          }
          size="large"
          onClick={() => form.submit()}
          type="primary"
        >
          Actualizar Franquicia
        </Button>
      </Row>
      <HolidayCalendar
        visible={visible}
        locationId={editingLocation?.id}
        refetchLocations={refetchLocations}
        setVisible={setVisible}
        holidays={holidays}
        setHolidays={setHolidays}
      />
    </Drawer>
  );
};

export default EditLocationForm;
